import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';
import { NiceLineChart, RankedBar, Treemap } from '../shared/charts';
import { Page, Crumbs, Tabs, Card, Btn, Pill, DataTable, ui, type Col } from '../shared/ui';
import { readUrlParam, useUrlSync } from '../shared/urlState';

type Locale = 'hr' | 'en';

type BuybackBlock = {
    label: string;
    count: number;
    amount_eur: number;
    estimated_ruc_eur: number;
    estimated_ruc_pct: number | null;
    ruc_note: string;
    period: { from: string | null; to: string | null };
    top_partners: Array<{ customer_code: string; customer_name: string; cnt: number; amt: number }>;
    monthly: Array<{ period: string; cnt: number; amt: number }>;
    refinery_storno_excluded?: { count: number; amount_eur: number; note: string };
    tca_inflow?: { count: number; gross_eur: number; storno_eur: number; net_eur: number; note: string };
};

type OperativeRow = {
    id: number; type: 'investment' | 'lom'; txn_at: string; kasa_name: string | null;
    doc_ref: string; amount_eur: number; gold_fine_g: number | null; silver_fine_g: number | null; ruc_eur: number | null;
};
type BuybackResponse = {
    date_from: string;
    date_to: string;
    blocks: { investment?: BuybackBlock; lom?: BuybackBlock };
    combined: { count: number; amount_eur: number; estimated_ruc_eur: number; estimated_ruc_pct: number | null };
    operative?: {
        available: boolean;
        investment?: { count: number; amount_eur: number; gold_fine_g: number; gold_qty: number };
        lom?: { count: number; gold_fine_g: number; silver_fine_g: number; nabavna_eur: number; prihod_eur: number; ruc_eur: number };
        combined?: { count: number; value_eur: number; ruc_eur: number; gold_fine_g: number; silver_fine_g: number };
        last_txn_at?: string | null;
        total_rows?: number;
    };
    operative_rows?: OperativeRow[];
};

type BuybackRow = {
    id: number;
    source: 'investment' | 'lom';
    company_code: string;
    invoice_number: string;
    document_date: string;
    partner_name: string | null;
    partner_code: string | null;
    amount_eur: number;
    doc_prefix: string | null;
};

type TabKey = 'all' | 'investment' | 'lom';

export function BuybackModule({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [tab, setTab] = useState<TabKey>(readUrlParam('tab', 'all') as TabKey);
    useUrlSync('tab', tab, tab === 'all');
    const today = new Date().toISOString().slice(0, 10);
    const yearStart = today.slice(0, 4) + '-01-01';
    const [dateFrom, setDateFrom] = useState(yearStart);
    const [dateTo, setDateTo] = useState(today);
    const [data, setData] = useState<BuybackResponse | null>(null);
    const [rows, setRows] = useState<BuybackRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, type: tab });
            const [agg, rowsResp] = await Promise.all([
                api<BuybackResponse>(`/buyback?${qs}`, token),
                api<{ rows: BuybackRow[]; count: number; total: number }>(`/buyback/rows?${qs}&limit=200`, token),
            ]);
            setData(agg);
            setRows(rowsResp.rows);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally { setLoading(false); }
    }, [token, dateFrom, dateTo, tab]);

    useEffect(() => { void load(); }, [load]);

    const [flagged, setFlagged] = useState<Set<number>>(new Set());
    const toggleForecast = async (id: number) => {
        try {
            const res = await fetch(`/api/buyback/${id}/forecast-flag`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            const j = await res.json();
            if (res.ok) setFlagged((p) => { const n = new Set(p); j.excluded_from_forecast ? n.add(id) : n.delete(id); return n; });
        } catch { /* ignore */ }
    };

    const setRange = (preset: 'this_month' | 'last_30d' | 'this_quarter' | 'ytd' | 'last_year') => {
        const now = new Date();
        const fmt = (d: Date) => d.toISOString().slice(0, 10);
        switch (preset) {
            case 'this_month': setDateFrom(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`); setDateTo(fmt(now)); break;
            case 'last_30d': { const s = new Date(); s.setDate(s.getDate() - 30); setDateFrom(fmt(s)); setDateTo(fmt(now)); break; }
            case 'this_quarter': { const q = Math.floor(now.getMonth() / 3); setDateFrom(`${now.getFullYear()}-${String(q*3+1).padStart(2,'0')}-01`); setDateTo(fmt(now)); break; }
            case 'ytd': setDateFrom(`${now.getFullYear()}-01-01`); setDateTo(fmt(now)); break;
            case 'last_year': setDateFrom(`${now.getFullYear()-1}-01-01`); setDateTo(`${now.getFullYear()-1}-12-31`); break;
        }
    };

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    if (isHrms) {
        return (
            <Page>
                <Crumbs items={[t('Dashboard', 'Dashboard'), t('Otkup zlata', 'Gold buyback')]} />
                <Tabs
                    tabs={[
                        { key: 'all', label: t('Sve', 'All'), icon: '🪙' },
                        { key: 'investment', label: t('Investicijsko (ADO)', 'Investment (ADO)') },
                        { key: 'lom', label: t('Lom (ADMS)', 'Scrap (ADMS)') },
                    ]}
                    active={tab}
                    onChange={(k) => setTab(k as TabKey)}
                />

                <Card pad={14}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.84rem' }}>
                        <label style={{ color: ui.ink2 }}>{t('Od', 'From')}: <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '6px 9px', borderRadius: 8, border: `1px solid ${ui.line}` }} /></label>
                        <label style={{ color: ui.ink2 }}>{t('Do', 'To')}: <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '6px 9px', borderRadius: 8, border: `1px solid ${ui.line}` }} /></label>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {([
                                { k: 'this_month', l: t('Mjesec', 'Month') },
                                { k: 'last_30d', l: '30d' },
                                { k: 'this_quarter', l: 'Q' },
                                { k: 'ytd', l: 'YTD' },
                                { k: 'last_year', l: t('Prošla godina', 'Last year') },
                            ] as const).map((p) => (
                                <Btn key={p.k} size="sm" variant="soft" onClick={() => setRange(p.k)}>{p.l}</Btn>
                            ))}
                        </div>
                    </div>
                </Card>

                {error ? <div style={{ color: '#dc2626', fontSize: '0.86rem', padding: '0 4px' }}>{error}</div> : null}
                {loading ? <div style={{ color: ui.muted, padding: '0 4px' }}>{t('Učitavam…', 'Loading…')}</div> : null}

                {!loading && data ? <BuybackBody data={data} rows={rows} locale={locale} tab={tab} isHrms /> : null}
            </Page>
        );
    }

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🪙</div>
                <div className="bi-module-hero-text">
                    <h1>{t('Otkup zlata', 'Gold buyback')}</h1>
                    <p className="muted">
                        {t(
                            'Agregat otkupa: investicijsko zlato (ADO, IRA prefix 2) + lom zlata (ADMS, IRA u minusu). RUC procjenjen povijesnom maržom.',
                            'Buyback aggregate: investment gold (ADO, IRA prefix 2) + scrap (ADMS, negative IRA). RUC estimated by historical margin.',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {(['all', 'investment', 'lom'] as const).map((k) => {
                    const active = tab === k;
                    return (
                        <button key={k} type="button" onClick={() => setTab(k)}
                            style={{
                                padding: '6px 14px', borderRadius: 6,
                                border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                                color: active ? '#fffbeb' : 'var(--ad-ink)',
                                fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer',
                            }}>
                            {k === 'all' ? t('Sve', 'All') : k === 'investment' ? t('Investicijsko (ADO)', 'Investment (ADO)') : t('Lom (ADMS)', 'Scrap (ADMS)')}
                        </button>
                    );
                })}
            </div>

            {/* Date controls */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.84rem' }}>
                <label>{t('Od', 'From')}: <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} /></label>
                <label>{t('Do', 'To')}: <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} /></label>
                <div style={{ display: 'flex', gap: 4 }}>
                    {([
                        { k: 'this_month', l: t('Mjesec', 'Month') },
                        { k: 'last_30d', l: '30d' },
                        { k: 'this_quarter', l: 'Q' },
                        { k: 'ytd', l: 'YTD' },
                        { k: 'last_year', l: t('Prošla godina', 'Last year') },
                    ] as const).map((p) => (
                        <button key={p.k} type="button" onClick={() => setRange(p.k)} className="secondary compact" style={{ fontSize: '0.76rem' }}>{p.l}</button>
                    ))}
                </div>
            </div>

            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {!loading && data ? (
                <>
                    {/* Combined KPI strip — naslov koristi UŽIVO (15-min) podatke kad ih ima, inače GL. */}
                    {(() => {
                        const oc = data.operative?.available ? data.operative.combined : undefined;
                        const useOp = !!oc && (data.operative?.total_rows ?? 0) > 0;
                        const cnt = useOp ? oc!.count : data.combined.count;
                        const val = useOp ? oc!.value_eur : data.combined.amount_eur;
                        const ruc = useOp ? oc!.ruc_eur : data.combined.estimated_ruc_eur;
                        const rucPct = useOp ? (val > 0 ? (100 * ruc / val) : null) : data.combined.estimated_ruc_pct;
                        return (
                            <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ad-acc-3)', marginBottom: 8, fontWeight: 700 }}>
                                    {useOp ? `📡 ${t('Uživo — 15-min izvještaji', 'Live — 15-min reports')}` : `📒 ${t('Knjigovodstvo (GL)', 'Accounting (GL)')}`}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                                    <KpiBox label={t('Broj otkupa', 'Buyback count')} value={formatNumber(cnt, locale)} />
                                    <KpiBox label={t('Vrijednost otkupa', 'Buyback value')} value={formatMoney(val, locale)} accent="#b14040" />
                                    <KpiBox label={useOp ? t('RUC (lom)', 'RUC (scrap)') : t('Procjena RUC', 'Estimated RUC')} value={formatMoney(ruc, locale)} accent="#1f7a36" />
                                    <KpiBox label={t('RUC %', 'RUC %')} value={rucPct != null ? `${rucPct.toFixed(2)}%` : '—'} accent="var(--ad-acc-3)" />
                                </div>
                            </div>
                        );
                    })()}

                    {/* Per-source blocks */}
                    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: tab === 'all' ? 'repeat(auto-fit, minmax(420px, 1fr))' : '1fr', marginBottom: 16 }}>
                        {data.blocks.investment ? <BlockCard b={data.blocks.investment} locale={locale} accent="var(--ad-acc)" /> : null}
                        {data.blocks.lom ? <BlockCard b={data.blocks.lom} locale={locale} accent="#1e40af" /> : null}
                    </div>

                    {/* Operativni otkup — 15-min Sales & Buyback izvještaji (auto-ingest s obrada maila) */}
                    {data.operative?.available && (data.operative.total_rows ?? 0) > 0 ? (
                        <div style={{ border: '1px solid rgba(var(--ad-acc-rgb),0.4)', borderRadius: 12, padding: 14, marginBottom: 16, background: 'rgba(var(--ad-acc-rgb),0.04)' }}>
                            <h3 style={{ margin: '0 0 2px', fontSize: '0.82rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>📡 {t('Operativni otkup (15-min izvještaji)', 'Operative buyback (15-min reports)')}</h3>
                            <div className="muted" style={{ fontSize: '0.74rem', marginBottom: 10 }}>{t('Auto-uvoz s obrada maila', 'Auto-imported from obrada mail')}{data.operative.last_txn_at ? ' · ' + t('zadnje', 'last') + ': ' + new Date(data.operative.last_txn_at).toLocaleString('hr-HR') : ''} · {data.operative.total_rows ?? 0} {t('zapisa', 'records')}</div>
                            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                                <div style={{ border: '1px solid rgba(var(--ad-acc-rgb),0.3)', borderRadius: 10, padding: 12, background: '#fff' }}>
                                    <div style={{ fontWeight: 800, color: 'var(--ad-acc)', marginBottom: 6 }}>🪙 {t('Investicijsko', 'Investment')}</div>
                                    <div style={{ fontSize: '0.84rem' }}>{data.operative.investment?.count ?? 0} {t('otkupa', 'buybacks')} · {formatNumber(data.operative.investment?.gold_fine_g ?? 0, locale)} g fine · <strong>{formatMoney(data.operative.investment?.amount_eur ?? 0, locale)}</strong></div>
                                </div>
                                <div style={{ border: '1px solid rgba(30,64,175,0.3)', borderRadius: 10, padding: 12, background: '#fff' }}>
                                    <div style={{ fontWeight: 800, color: '#1e40af', marginBottom: 6 }}>🔨 {t('Lomljeno', 'Scrap')}</div>
                                    <div style={{ fontSize: '0.84rem' }}>{data.operative.lom?.count ?? 0} {t('otkupa', 'buybacks')} · {formatNumber(data.operative.lom?.gold_fine_g ?? 0, locale)} g Au · {formatNumber(data.operative.lom?.silver_fine_g ?? 0, locale)} g Ag</div>
                                    <div style={{ fontSize: '0.82rem', marginTop: 4 }}>{t('Nabavna', 'Cost')} {formatMoney(data.operative.lom?.nabavna_eur ?? 0, locale)} · {t('Prihod', 'Revenue')} {formatMoney(data.operative.lom?.prihod_eur ?? 0, locale)} · RUC <strong style={{ color: '#1f7a36' }}>{formatMoney(data.operative.lom?.ruc_eur ?? 0, locale)}</strong></div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Recent buyback rows — UŽIVO 15-min transakcije ako ih ima, inače GL (povijesno). */}
                    {(() => {
                        const opRows = data.operative_rows ?? [];
                        if (opRows.length > 0) {
                            return (
                                <>
                                    <h3 style={{ margin: '14px 0 8px', fontSize: '0.82rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                                        📡 {t('Posljednje transakcije (15-min uživo)', 'Recent transactions (15-min live)')} ({opRows.length})
                                    </h3>
                                    <div className="table-wrap">
                                        <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('Vrijeme', 'Time')}</th>
                                                    <th>{t('Tip', 'Type')}</th>
                                                    <th>{t('Kasa', 'Register')}</th>
                                                    <th>{t('Dokument', 'Doc')}</th>
                                                    <th style={{ textAlign: 'right' }}>{t('Fine g', 'Fine g')}</th>
                                                    <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                                    <th style={{ textAlign: 'right' }}>RUC</th>
                                                    <th style={{ textAlign: 'center' }} title={t('Izuzmi outlier iz sales forecasta', 'Exclude outlier from sales forecast')}>{t('Forecast', 'Forecast')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {opRows.map((r) => (
                                                    <tr key={`${r.type}-${r.id}`} style={flagged.has(r.id) ? { opacity: 0.55 } : undefined}>
                                                        <td className="muted" style={{ whiteSpace: 'nowrap' }}>{(r.txn_at ?? '').slice(0, 16).replace('T', ' ')}</td>
                                                        <td>
                                                            <span style={{ background: r.type === 'investment' ? 'rgba(var(--ad-acc-rgb),0.18)' : 'rgba(30,64,175,0.15)', color: r.type === 'investment' ? 'var(--ad-acc-3)' : '#1e40af', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700 }}>
                                                                {r.type === 'investment' ? t('Investicijsko', 'Investment') : t('Lom', 'Scrap')}
                                                            </span>
                                                        </td>
                                                        <td>{r.kasa_name ?? <span className="muted">—</span>}</td>
                                                        <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>{r.doc_ref}</td>
                                                        <td style={{ textAlign: 'right' }}>{r.gold_fine_g != null ? formatNumber(r.gold_fine_g, locale) : '—'}{r.silver_fine_g ? ` / ${formatNumber(r.silver_fine_g, locale)} Ag` : ''}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#b14040' }}>{formatMoney(r.amount_eur, locale)}</td>
                                                        <td style={{ textAlign: 'right', color: '#1f7a36' }}>{r.ruc_eur != null ? formatMoney(r.ruc_eur, locale) : '—'}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => void toggleForecast(r.id)} title={t('Izuzmi/vrati u forecast', 'Exclude/include in forecast')}
                                                                style={{ cursor: 'pointer', border: '1px solid ' + (flagged.has(r.id) ? '#b14040' : 'rgba(var(--ad-soft-rgb),0.4)'), background: flagged.has(r.id) ? 'rgba(177,64,64,0.12)' : 'transparent', color: flagged.has(r.id) ? '#b14040' : '#8a7a55', borderRadius: 6, padding: '2px 7px', fontSize: '0.72rem', fontWeight: 700 }}>
                                                                {flagged.has(r.id) ? '🚫 ' + t('izuzet', 'excluded') : t('izuzmi', 'exclude')}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            );
                        }
                        return (
                            <>
                                <h3 style={{ margin: '14px 0 8px', fontSize: '0.82rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                                    📒 {t('Posljednje transakcije (knjigovodstvo)', 'Recent transactions (GL)')} ({rows.length})
                                </h3>
                                <div className="table-wrap">
                                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                                        <thead>
                                            <tr>
                                                <th>{t('Datum', 'Date')}</th>
                                                <th>{t('Tip', 'Type')}</th>
                                                <th>{t('Tvrtka', 'Co.')}</th>
                                                <th>{t('Broj IRA', 'Invoice #')}</th>
                                                <th>{t('Partner', 'Partner')}</th>
                                                <th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r) => (
                                                <tr key={`${r.source}-${r.id}`}>
                                                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.document_date, locale)}</td>
                                                    <td>
                                                        <span style={{ background: r.source === 'investment' ? 'rgba(var(--ad-acc-rgb),0.18)' : 'rgba(30,64,175,0.15)', color: r.source === 'investment' ? 'var(--ad-acc-3)' : '#1e40af', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700 }}>
                                                            {r.source === 'investment' ? t('Investicijsko', 'Investment') : t('Lom', 'Scrap')}
                                                        </span>
                                                    </td>
                                                    <td>{r.company_code}</td>
                                                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>{r.invoice_number}</td>
                                                    <td>{r.partner_name ?? <span className="muted">—</span>} {r.partner_code ? <span className="muted" style={{ fontSize: '0.72rem' }}>({r.partner_code})</span> : null}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#b14040' }}>{formatMoney(r.amount_eur, locale)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        );
                    })()}
                </>
            ) : null}
        </section>
    );
}

// ── HRMS body — isti sadržaj kao legacy, ali na UI kitu (Card + DataTable + Pill).
// Legacy (gold) putanja se NE dira; ovo se renderira samo u hrms grani.
function BuybackBody({ data, rows, locale, tab, isHrms }: { data: BuybackResponse; rows: BuybackRow[]; locale: Locale; tab: TabKey; isHrms: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    void isHrms;

    const oc = data.operative?.available ? data.operative.combined : undefined;
    const useOp = !!oc && (data.operative?.total_rows ?? 0) > 0;
    const cnt = useOp ? oc!.count : data.combined.count;
    const val = useOp ? oc!.value_eur : data.combined.amount_eur;
    const ruc = useOp ? oc!.ruc_eur : data.combined.estimated_ruc_eur;
    const rucPct = useOp ? (val > 0 ? (100 * ruc / val) : null) : data.combined.estimated_ruc_pct;

    const opRows = data.operative_rows ?? [];

    const opCols: Col<OperativeRow>[] = [
        { key: 'txn_at', label: t('Vrijeme', 'Time'), render: (r) => <span style={{ color: ui.muted, whiteSpace: 'nowrap' }}>{(r.txn_at ?? '').slice(0, 16).replace('T', ' ')}</span> },
        { key: 'type', label: t('Tip', 'Type'), render: (r) => <Pill tone={r.type === 'investment' ? 'info' : 'neutral'}>{r.type === 'investment' ? t('Investicijsko', 'Investment') : t('Lom', 'Scrap')}</Pill> },
        { key: 'kasa_name', label: t('Kasa', 'Register'), render: (r) => r.kasa_name ?? <span style={{ color: ui.muted }}>—</span> },
        { key: 'doc_ref', label: t('Dokument', 'Doc'), render: (r) => <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>{r.doc_ref}</span> },
        { key: 'fine', label: t('Fine g', 'Fine g'), align: 'right', render: (r) => <>{r.gold_fine_g != null ? formatNumber(r.gold_fine_g, locale) : '—'}{r.silver_fine_g ? ` / ${formatNumber(r.silver_fine_g, locale)} Ag` : ''}</> },
        { key: 'amount_eur', label: t('Iznos', 'Amount'), align: 'right', render: (r) => <span style={{ fontWeight: 600, color: '#b14040' }}>{formatMoney(r.amount_eur, locale)}</span> },
        { key: 'ruc_eur', label: 'RUC', align: 'right', render: (r) => <span style={{ color: '#1f7a36' }}>{r.ruc_eur != null ? formatMoney(r.ruc_eur, locale) : '—'}</span> },
    ];

    const glCols: Col<BuybackRow>[] = [
        { key: 'document_date', label: t('Datum', 'Date'), render: (r) => <span style={{ color: ui.muted, whiteSpace: 'nowrap' }}>{formatDate(r.document_date, locale)}</span> },
        { key: 'source', label: t('Tip', 'Type'), render: (r) => <Pill tone={r.source === 'investment' ? 'info' : 'neutral'}>{r.source === 'investment' ? t('Investicijsko', 'Investment') : t('Lom', 'Scrap')}</Pill> },
        { key: 'company_code', label: t('Tvrtka', 'Co.'), render: (r) => r.company_code },
        { key: 'invoice_number', label: t('Broj IRA', 'Invoice #'), render: (r) => <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>{r.invoice_number}</span> },
        { key: 'partner_name', label: t('Partner', 'Partner'), render: (r) => <>{r.partner_name ?? <span style={{ color: ui.muted }}>—</span>} {r.partner_code ? <span style={{ color: ui.muted, fontSize: '0.72rem' }}>({r.partner_code})</span> : null}</> },
        { key: 'amount_eur', label: t('Iznos', 'Amount'), align: 'right', render: (r) => <span style={{ fontWeight: 600, color: '#b14040' }}>{formatMoney(r.amount_eur, locale)}</span> },
    ];

    return (
        <>
            {/* Combined KPI strip — naslov koristi UŽIVO (15-min) podatke kad ih ima, inače GL. */}
            <Card pad={16}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: ui.muted, marginBottom: 10, fontWeight: 700 }}>
                    {useOp ? `📡 ${t('Uživo — 15-min izvještaji', 'Live — 15-min reports')}` : `📒 ${t('Knjigovodstvo (GL)', 'Accounting (GL)')}`}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                    <KpiBox label={t('Broj otkupa', 'Buyback count')} value={formatNumber(cnt, locale)} />
                    <KpiBox label={t('Vrijednost otkupa', 'Buyback value')} value={formatMoney(val, locale)} accent="#b14040" />
                    <KpiBox label={useOp ? t('RUC (lom)', 'RUC (scrap)') : t('Procjena RUC', 'Estimated RUC')} value={formatMoney(ruc, locale)} accent="#1f7a36" />
                    <KpiBox label={t('RUC %', 'RUC %')} value={rucPct != null ? `${rucPct.toFixed(2)}%` : '—'} accent={ui.acc} />
                </div>
            </Card>

            {/* Per-source blocks */}
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: tab === 'all' ? 'repeat(auto-fit, minmax(420px, 1fr))' : '1fr' }}>
                {data.blocks.investment ? <BlockCard b={data.blocks.investment} locale={locale} accent="var(--ad-acc)" /> : null}
                {data.blocks.lom ? <BlockCard b={data.blocks.lom} locale={locale} accent="#1e40af" /> : null}
            </div>

            {/* Operativni otkup — 15-min Sales & Buyback izvještaji (auto-ingest s obrada maila) */}
            {data.operative?.available && (data.operative.total_rows ?? 0) > 0 ? (
                <Card pad={14}>
                    <h3 style={{ margin: '0 0 2px', fontSize: '0.82rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: ui.acc }}>📡 {t('Operativni otkup (15-min izvještaji)', 'Operative buyback (15-min reports)')}</h3>
                    <div style={{ color: ui.muted, fontSize: '0.74rem', marginBottom: 10 }}>{t('Auto-uvoz s obrada maila', 'Auto-imported from obrada mail')}{data.operative.last_txn_at ? ' · ' + t('zadnje', 'last') + ': ' + new Date(data.operative.last_txn_at).toLocaleString('hr-HR') : ''} · {data.operative.total_rows ?? 0} {t('zapisa', 'records')}</div>
                    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                        <div style={{ border: `1px solid ${ui.line}`, borderRadius: 10, padding: 12, background: '#fff' }}>
                            <div style={{ fontWeight: 800, color: 'var(--ad-acc)', marginBottom: 6 }}>🪙 {t('Investicijsko', 'Investment')}</div>
                            <div style={{ fontSize: '0.84rem' }}>{data.operative.investment?.count ?? 0} {t('otkupa', 'buybacks')} · {formatNumber(data.operative.investment?.gold_fine_g ?? 0, locale)} g fine · <strong>{formatMoney(data.operative.investment?.amount_eur ?? 0, locale)}</strong></div>
                        </div>
                        <div style={{ border: `1px solid ${ui.line}`, borderRadius: 10, padding: 12, background: '#fff' }}>
                            <div style={{ fontWeight: 800, color: '#1e40af', marginBottom: 6 }}>🔨 {t('Lomljeno', 'Scrap')}</div>
                            <div style={{ fontSize: '0.84rem' }}>{data.operative.lom?.count ?? 0} {t('otkupa', 'buybacks')} · {formatNumber(data.operative.lom?.gold_fine_g ?? 0, locale)} g Au · {formatNumber(data.operative.lom?.silver_fine_g ?? 0, locale)} g Ag</div>
                            <div style={{ fontSize: '0.82rem', marginTop: 4 }}>{t('Nabavna', 'Cost')} {formatMoney(data.operative.lom?.nabavna_eur ?? 0, locale)} · {t('Prihod', 'Revenue')} {formatMoney(data.operative.lom?.prihod_eur ?? 0, locale)} · RUC <strong style={{ color: '#1f7a36' }}>{formatMoney(data.operative.lom?.ruc_eur ?? 0, locale)}</strong></div>
                        </div>
                    </div>
                </Card>
            ) : null}

            {/* Recent buyback rows — UŽIVO 15-min transakcije ako ih ima, inače GL (povijesno). */}
            {opRows.length > 0 ? (
                <div>
                    <h3 style={{ margin: '4px 0 8px', fontSize: '0.82rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: ui.acc }}>
                        📡 {t('Posljednje transakcije (15-min uživo)', 'Recent transactions (15-min live)')} ({opRows.length})
                    </h3>
                    <Card pad={0}>
                        <DataTable cols={opCols} rows={opRows} rowKey={(r) => `${r.type}-${r.id}`} empty={t('Nema transakcija.', 'No transactions.')} />
                    </Card>
                </div>
            ) : (
                <div>
                    <h3 style={{ margin: '4px 0 8px', fontSize: '0.82rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: ui.acc }}>
                        📒 {t('Posljednje transakcije (knjigovodstvo)', 'Recent transactions (GL)')} ({rows.length})
                    </h3>
                    <Card pad={0}>
                        <DataTable cols={glCols} rows={rows} rowKey={(r) => `${r.source}-${r.id}`} empty={t('Nema transakcija.', 'No transactions.')} />
                    </Card>
                </div>
            )}
        </>
    );
}

function KpiBox({ label, value, accent }: { label: string; value: string; accent?: string }) {
    return (
        <div style={{ background: '#fff', borderLeft: `3px solid ${accent ?? 'var(--ad-acc)'}`, padding: '10px 14px', borderRadius: 4 }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{label}</div>
            <strong style={{ fontSize: '1.1rem', color: accent ?? '#34383d' }}>{value}</strong>
        </div>
    );
}

function BlockCard({ b, locale, accent }: { b: BuybackBlock; locale: Locale; accent: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ borderBottom: `2px solid ${accent}`, paddingBottom: 8, marginBottom: 10 }}>
                <h3 style={{ margin: 0, color: accent, fontSize: '1.05rem' }}>{b.label}</h3>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.74rem', fontStyle: 'italic' }}>{b.ruc_note}</p>
                {b.refinery_storno_excluded && b.refinery_storno_excluded.count > 0 ? (
                    <p style={{ margin: '6px 0 0', fontSize: '0.74rem', color: 'var(--ad-acc-3)', background: 'rgba(var(--ad-acc-rgb),0.10)', padding: '6px 8px', borderRadius: 4 }}>
                        ⚠ {t('Isključeno', 'Excluded')}: <strong>{b.refinery_storno_excluded.count}</strong> {t('storno rafinerije', 'refinery storno')} ({formatMoney(b.refinery_storno_excluded.amount_eur, locale)}).{' '}
                        <span className="muted">{b.refinery_storno_excluded.note}</span>
                    </p>
                ) : null}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                <KpiBox label={t('Broj otkupa', 'Buyback count')} value={formatNumber(b.count, locale)} accent={accent} />
                <KpiBox label={t('Otkupna vrijednost', 'Cost basis')} value={formatMoney(b.amount_eur, locale)} accent={accent} />
                {b.tca_inflow ? (
                    <>
                        <KpiBox label={t('Prodaja TCA (neto)', 'TCA sale (net)')} value={formatMoney(b.tca_inflow.net_eur, locale)} accent="#1f7a36" />
                        <KpiBox label={t('Razlika (RUC)', 'Difference (RUC)')} value={formatMoney(b.estimated_ruc_eur, locale)} accent={b.estimated_ruc_eur >= 0 ? '#1f7a36' : '#b14040'} />
                        <KpiBox label={t('Markup na nabavu', 'Markup on cost')} value={b.estimated_ruc_pct != null ? `${b.estimated_ruc_pct.toFixed(1)}%` : '—'} accent={accent} />
                    </>
                ) : (
                    <>
                        <KpiBox label={t('Procjena RUC', 'Estimated RUC')} value={formatMoney(b.estimated_ruc_eur, locale)} accent={accent} />
                        <KpiBox label={t('RUC %', 'RUC %')} value={b.estimated_ruc_pct != null ? `${b.estimated_ruc_pct.toFixed(1)}%` : '—'} accent={accent} />
                    </>
                )}
            </div>

            {b.monthly.length > 0 ? (
                <>
                    <h4 style={{ margin: '8px 0 6px', fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Mjesečni trend', 'Monthly trend')}</h4>
                    <NiceLineChart
                        locale={locale}
                        height={160}
                        xLabels={b.monthly.map((m) => m.period)}
                        series={[{ name: t('Iznos otkupa', 'Buyback amount'), color: accent, values: b.monthly.map((m) => m.amt) }]}
                        yFormatter={(n) => n >= 1e6 ? (n/1e6).toFixed(1)+'M' : n >= 1e3 ? (n/1e3).toFixed(0)+'k' : String(Math.round(n))}
                    />
                </>
            ) : null}

            {b.top_partners.length > 0 ? (
                <>
                    <h4 style={{ margin: '14px 0 6px', fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Top 10 partnera', 'Top 10 partners')}</h4>
                    <RankedBar
                        locale={locale}
                        maxBars={10}
                        barColor={accent}
                        items={b.top_partners.map((p) => ({
                            label: p.customer_name || p.customer_code || '—',
                            value: p.amt,
                            sub: `${p.cnt} ${t('IRA', 'inv.')}`,
                        }))}
                        moneyFormatter={(n) => formatMoney(n, locale)}
                    />
                </>
            ) : null}
        </div>
    );
}
